import React, { useState, useRef } from 'react';
import { Box, Button, Typography, Paper, LinearProgress } from '@mui/material';
import MicIcon from '@mui/icons-material/Mic';
import StopIcon from '@mui/icons-material/Stop';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';

interface AudioInputProps {
  onAudioData: (audioBlob: Blob) => void;
  isProcessing: boolean;
  disabled?: boolean;
}

const AudioInput: React.FC<AudioInputProps> = ({ onAudioData, isProcessing, disabled }) => {
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [isConverting, setIsConverting] = useState(false);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const timerRef = useRef<number | null>(null);

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;
      chunksRef.current = [];

      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          chunksRef.current.push(e.data);
        }
      };

      mediaRecorder.onstop = () => {
        const audioBlob = new Blob(chunksRef.current, { type: 'audio/webm' });
        onAudioData(audioBlob);
        stream.getTracks().forEach(track => track.stop());
        if (timerRef.current) {
          clearInterval(timerRef.current);
        }
        setRecordingTime(0);
      };

      mediaRecorder.start();
      setIsRecording(true);
      
      // Start timer
      timerRef.current = window.setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);
    } catch (error) {
      console.error('Error accessing microphone:', error);
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
  };

  const convertToWebM = async (file: File): Promise<Blob> => {
    try {
      // Create audio context
      const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
      
      // Read the file
      const arrayBuffer = await file.arrayBuffer();
      
      // Decode the audio
      const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);
      
      // Create media stream destination
      const destination = audioContext.createMediaStreamDestination();
      
      // Create buffer source
      const source = audioContext.createBufferSource();
      source.buffer = audioBuffer;
      source.connect(destination);
      
      // Create MediaRecorder
      const mediaRecorder = new MediaRecorder(destination.stream);
      const chunks: Blob[] = [];
      
      return new Promise((resolve, reject) => {
        mediaRecorder.ondataavailable = (e) => {
          if (e.data.size > 0) {
            chunks.push(e.data);
          }
        };
        
        mediaRecorder.onstop = () => {
          const webmBlob = new Blob(chunks, { type: 'audio/webm' });
          resolve(webmBlob);
        };
        
        mediaRecorder.onerror = (err) => {
          reject(err);
        };
        
        // Start recording
        mediaRecorder.start();
        source.start(0);
        
        // Stop recording when the audio ends
        source.onended = () => {
          mediaRecorder.stop();
        };
        
        // Set a timeout in case onended doesn't fire
        setTimeout(() => {
          if (mediaRecorder.state === 'recording') {
            mediaRecorder.stop();
          }
        }, Math.ceil(audioBuffer.duration * 1000) + 100);
      });
    } catch (error) {
      console.error('Error converting audio:', error);
      throw error;
    }
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      try {
        setIsConverting(true);
        // Convert the file to WebM format
        const webmBlob = await convertToWebM(file);
        onAudioData(webmBlob);
      } catch (error) {
        console.error('Error processing audio file:', error);
        // If conversion fails, try using the original file
        onAudioData(file);
      } finally {
        setIsConverting(false);
      }
    }
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <Paper 
      elevation={0} 
      sx={{ 
        p: 3, 
        mb: 3,
        background: isRecording 
          ? 'linear-gradient(45deg, rgba(239, 68, 68, 0.15), rgba(248, 113, 113, 0.15))'
          : 'linear-gradient(45deg, rgba(25, 118, 210, 0.05), rgba(66, 165, 245, 0.05))',
        backdropFilter: 'blur(10px)',
        border: '1px solid',
        borderColor: isRecording ? 'error.main' : 'primary.main',
        borderRadius: 3,
        transition: 'all 0.3s ease',
        opacity: disabled ? 0.7 : 1,
      }}
    >
      <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
        Audio Input
      </Typography>
      
      <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 2 }}>
        <Button
          variant={isRecording ? "contained" : "outlined"}
          color={isRecording ? "error" : "primary"}
          startIcon={isRecording ? <StopIcon /> : <MicIcon />}
          onClick={isRecording ? stopRecording : startRecording}
          size="large"
          sx={{ 
            minWidth: 200,
            py: 1.5,
            borderRadius: 2,
            ...(isRecording && {
              animation: 'pulse 2s infinite',
              '@keyframes pulse': {
                '0%': {
                  boxShadow: '0 0 0 0 rgba(239, 68, 68, 0.4)',
                },
                '70%': {
                  boxShadow: '0 0 0 10px rgba(239, 68, 68, 0)',
                },
                '100%': {
                  boxShadow: '0 0 0 0 rgba(239, 68, 68, 0)',
                },
              },
            })
          }}
          disabled={isProcessing || isConverting || disabled}
        >
          {isRecording ? 'Stop Recording' : 'Start Recording'}
        </Button>
        
        <Button
          variant="outlined"
          component="label"
          startIcon={isConverting ? <CloudUploadIcon /> : <UploadFileIcon />}
          size="large"
          sx={{ 
            minWidth: 200,
            py: 1.5,
            borderRadius: 2,
            borderColor: 'divider',
            '&:hover': {
              borderColor: 'primary.main',
            }
          }}
          disabled={isProcessing || isConverting || isRecording || disabled}
        >
          {isConverting ? 'Converting...' : 'Upload Audio File'}
          <input
            type="file"
            hidden
            accept="audio/*"
            onChange={handleFileUpload}
            disabled={isProcessing || isConverting || isRecording || disabled}
          />
        </Button>
      </Box>

      {(isRecording || isConverting) && (
        <Box sx={{ mt: 3 }}>
          {isRecording && (
            <>
              <Typography 
                variant="body2" 
                sx={{ 
                  color: 'error.main',
                  fontWeight: 500,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  mb: 1
                }}
              >
                <span style={{ 
                  display: 'inline-block',
                  width: 8,
                  height: 8,
                  backgroundColor: '#ef4444',
                  borderRadius: '50%',
                  animation: 'blink 1s infinite'
                }} />
                Recording time: {formatTime(recordingTime)}
              </Typography>
              <LinearProgress 
                variant="determinate" 
                value={(recordingTime % 60) * (100/60)} 
                color="error"
                sx={{ 
                  height: 6,
                  borderRadius: 3,
                  bgcolor: 'rgba(239, 68, 68, 0.1)',
                  '& .MuiLinearProgress-bar': {
                    borderRadius: 3,
                  },
                  '@keyframes blink': {
                    '0%': { opacity: 1 },
                    '50%': { opacity: 0.4 },
                    '100%': { opacity: 1 },
                  },
                }}
              />
            </>
          )}
          {isConverting && (
            <>
              <Typography 
                variant="body2" 
                sx={{ 
                  color: 'primary.main',
                  fontWeight: 500,
                  mb: 1
                }}
              >
                Converting audio file...
              </Typography>
              <LinearProgress 
                sx={{ 
                  height: 6,
                  borderRadius: 3,
                  bgcolor: 'rgba(25, 118, 210, 0.1)',
                  '& .MuiLinearProgress-bar': {
                    borderRadius: 3,
                  },
                }}
              />
            </>
          )}
        </Box>
      )}
    </Paper>
  );
};

export default AudioInput; 